import { graphql, DataValue } from '@apollo/client/react/hoc';
import * as React from 'react';
import { memoize } from 'lodash';
import type { ApolloError } from 'apollo-server-errors';

import { ChatContext } from 'chat/components/ChatContext';
import { addSequenceItemsToQueryResult } from '../utils/query';
import GET_ANNOUNCEMENT_STREAM_ITEMS from '../queries/GetAnnouncementStreamItems.gql';
import type {
  GetAnnouncementStreamItems as GetAnnouncementStreamItemsData,
  GetAnnouncementStreamItems_entityStreams,
  GetAnnouncementStreamItems_entityStreams_sequenceItems,
} from '../queries/__generated__/GetAnnouncementStreamItems';

export const FIRST_PAGE_VAR = {
  gapItemNextPageParams: '{"limit":20,"since_seq":0}',
};

export type AnnouncementStreamContainerChildrenProps = {
  loadGaps: (gaps: Array<GetAnnouncementStreamItems_entityStreams_sequenceItems>) => void;
  error: ApolloError;
  stream: GetAnnouncementStreamItems_entityStreams | null;
  loading: boolean;
  hasItems: boolean;
};

type Props = {
  data?: DataValue<GetAnnouncementStreamItemsData>;
  uuid: string;
  children: (childProps: AnnouncementStreamContainerChildrenProps) => React.ReactElement;
  onNewBottomItem: () => void;
};

class AnnouncementStreamContainer extends React.Component<Props, {}> {
  static contextType = ChatContext;
  context!: React.ContextType<typeof ChatContext>;

  componentDidMount() {
    if (this.context.setStreamItemsRefetch && this.props.data?.refetch) {
      // @ts-expect-error; TS is saying sometimes the data prop is undefined even though we have a condition to check its existence above
      this.context.setStreamItemsRefetch(() => this.props.data.refetch);
    }
  }

  componentDidUpdate(prevProps: Props) {
    const curr = this.getSequenceItems(this.props);
    const prev = this.getSequenceItems(prevProps);

    if (curr[0] !== prev[0]) {
      this.props.onNewBottomItem();
    }
  }

  isLoading = (props: Props): boolean => {
    return props.data?.loading || (props.data?.networkStatus ?? 7) < 7;
  };

  getSequenceItems = (
    props: Props
  ): Array<GetAnnouncementStreamItems_entityStreams_sequenceItems> => {
    const stream = this.getStream(props);

    if (stream && stream.uuid === this.props.uuid) {
      return stream.sequenceItems ? [...stream.sequenceItems] : [];
    } else {
      return [];
    }
  };

  getStream = (props: Props): GetAnnouncementStreamItems_entityStreams | null => {
    const stream = props.data?.entityStreams?.[0];

    if (stream && stream.uuid === this.props.uuid) {
      return stream;
    } else {
      return null;
    }
  };

  loadGaps = (gaps: Array<GetAnnouncementStreamItems_entityStreams_sequenceItems>) => {
    for (const gap of gaps) {
      // @ts-expect-error ts-migrate(2349) FIXME: This expression is not callable.
      this.loadGap(gap);
    }
  };

  // memoize this function to avoid reloading a gap multiple time
  // when switching between chats.
  // @ts-expect-error ts-migrate(2558) FIXME: Expected 1 type arguments, but got 2.
  loadGap = memoize<Array<GetAnnouncementStreamItems_entityStreams_sequenceItems>, void>(
    (gap: GetAnnouncementStreamItems_entityStreams_sequenceItems) => {
      const { uuid } = this.props;
      const gapItemNextPageParams =
        gap.item.__typename === 'GapItem' ? gap.item.nextPageParams : undefined;

      this.props.data
        ?.fetchMore({
          variables: { uuid, gapItemNextPageParams },
          updateQuery: (previous, { fetchMoreResult }) => {
            if (!fetchMoreResult) {
              return previous;
            }

            return addSequenceItemsToQueryResult(
              previous,
              // @ts-expect-error
              fetchMoreResult.entityStreams[0].sequenceItems
            );
          },
        })
        .catch((e: any) => console.error('could not load announcement gap message: ', e));
    }
  );

  render() {
    const sequenceItems = this.getSequenceItems(this.props);
    return this.props.children({
      loadGaps: this.loadGaps,
      // @ts-expect-error
      error: this.props.data.error,
      stream: this.getStream(this.props),
      loading: this.isLoading(this.props),
      hasItems: sequenceItems.length > 0,
    });
  }
}

const WithMessages = graphql(GET_ANNOUNCEMENT_STREAM_ITEMS, {
  // @ts-expect-error ts-migrate(2339) FIXME: Property 'uuid' does not exist on type '{}'.
  options: ({ uuid }) => ({
    notifyOnNetworkStatusChange: true,
    variables: {
      uuid,
      ...FIRST_PAGE_VAR,
    },
  }),
});

export { AnnouncementStreamContainer };
// @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'typeof AnnouncementStreamContain... Remove this comment to see the full error message
export default WithMessages(AnnouncementStreamContainer);
